import React, { useEffect, useState } from 'react'
import { Spinner, Typography } from '@telavita-core/react-design-kit'

import { useDebounce } from 'hooks/useDebounce'
import { useAutoSuggestProfessionalName } from './hooks/useAutoSuggestProfessionalName'

import {
  Select,
  IOption,
  IActionMeta,
  ISingleValue,
  IInputActionMeta
} from 'components/ReactSelect'

import * as S from './styles'

interface ProfessionalNameFilterProps {
  professionalNameTerm: string
  onChangeProfessionalNameTerm: (term: string) => void
  hasError?: boolean
  clearFieldError?: () => void
  searchInfo: {
    profileRole: string
    startDate: string
    endDate: string
  }
}

const Loading = () => (
  <S.LoadingContainer>
    <Spinner customClassName='spinner'/>   
    <Typography variant='content2'>
      Buscando profissionais
    </Typography>
  </S.LoadingContainer>
)

export function ProfessionalNameFilter({
  professionalNameTerm,
  onChangeProfessionalNameTerm,
  hasError,
  clearFieldError,
  searchInfo
}: ProfessionalNameFilterProps): JSX.Element {
  const MIN_VALUE_TO_SEARCH_TEXT = 2

  const [searchText, setSearchText] = useState(professionalNameTerm)
  const [selectedProfessional, setSelectedProfessional] = useState<IOption | null>(null)

  const debouncedSearchText = useDebounce(searchText)
  const { professionalOptions, isLoading } = useAutoSuggestProfessionalName(debouncedSearchText, searchInfo)

  function handleSelectProfessional(professionalOption: ISingleValue<IOption>, { action }: IActionMeta<string>) {
    if (action === 'clear') {
      setSelectedProfessional(null)
      onChangeProfessionalNameTerm('')
    }

    if (!professionalOption) return null

    const selectedProfessional = professionalOptions.find(professional => professional.value === professionalOption.value)
    setSelectedProfessional(selectedProfessional)
    onChangeProfessionalNameTerm(selectedProfessional?.label)
  }

  function handleInputChange(newValue: string, { action }: IInputActionMeta) {
    if (action !== 'input-blur' && action !== 'menu-close') {
      setSearchText(newValue)
      onChangeProfessionalNameTerm(newValue)
    }

    if (action === 'input-change') {
      setSelectedProfessional({
        label: newValue,
        value: 0,
      })
    }
  }

  function handleClickClearIndicator() {
    setSearchText('')
    setSelectedProfessional(null)
    onChangeProfessionalNameTerm('')
    clearFieldError()
  }

  useEffect(() => {
    const filtersCleanHandled = professionalNameTerm === ''
    const hasSearchTextAndFiltersCleanHandled = filtersCleanHandled && searchText.length > 0

    if (hasSearchTextAndFiltersCleanHandled) {
      setSearchText('')
    }

    if (selectedProfessional !== null) {
      const hasSelectedProfessionalAndFiltersCleanHandled = filtersCleanHandled && Object?.values(selectedProfessional).length > 0
      
      if (hasSelectedProfessionalAndFiltersCleanHandled) {
        setSelectedProfessional(null)
      }
    }
  }, [professionalNameTerm, searchText, selectedProfessional])

  return (
    <S.ProfessionalNameFilterContainer>
      <Typography variant='content2' weight='bold'>
        Buscar profissional por nome
      </Typography>

      <div>
        <Select
          options={professionalOptions}
          isLoading={isLoading}
          onChange={(newValue: IOption, action: IActionMeta<string>) => handleSelectProfessional(newValue, action)}
          placeholder='Digite pelo menos 2 letras'
          loadingMessage={() => searchText.length < MIN_VALUE_TO_SEARCH_TEXT ? null : <Loading />}
          noOptionsMessage={() => 'Nenhum profissional encontrado'}
          onInputChange={handleInputChange}
          inputValue={searchText || selectedProfessional?.label || ''}
          value={selectedProfessional}
          hasError={hasError}
          showClearIndicator={!!searchText.length || !!selectedProfessional}
          onClickClearIndicator={handleClickClearIndicator}
        />

        {hasError && (
          <Typography variant='content3' color='danger'>
            Digite pelo menos 2 letras
          </Typography>
        )}
      </div>

    </S.ProfessionalNameFilterContainer>
  )
}